import { useState, useEffect, useRef } from "react"

interface UseDebouncedStringProps {
  initialValue: string
  onUpdate: (value: string) => void
  debounceMs?: number
  shouldUpdate?: boolean
}

const useDebouncedString = ({
  initialValue,
  onUpdate,
  debounceMs = 300,
  shouldUpdate = true,
}: UseDebouncedStringProps) => {
  const [value, setValue] = useState<string>(initialValue)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasInitializedWithRealData = useRef(false)

  // Update state with real initial data when it first becomes available
  useEffect(() => {
    if (!hasInitializedWithRealData.current && shouldUpdate) {
      setValue(initialValue)
      hasInitializedWithRealData.current = true
    }
  }, [initialValue, shouldUpdate])

  // Debounce and call onUpdate when value changes
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    if (!shouldUpdate) return

    const hasChanged = value !== initialValue

    if (hasChanged && (value || initialValue)) {
      debounceTimeoutRef.current = setTimeout(() => {
        onUpdate(value)
      }, debounceMs)
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [value, shouldUpdate, debounceMs, onUpdate, initialValue])

  return {
    value,
    setValue,
  }
}

export default useDebouncedString
